import { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { FormHeader } from '../components/FormHeader';
import { useNavigate } from 'react-router-dom';
import { useClaudeTasks } from '../features/claudeTasks/useClaudeTasks';
import { describeForm } from '../lib/formText';
import { useAction } from '../lib/useAction';
import { useNextSrb } from '../features/shipments/useShipments';
import { initialShipmentForm, type ShipmentForm } from '../features/shipments/form/shipmentForm';
import {
  BasicSection,
  CargoSection,
  ExtraSection,
  NotesSection,
  RouteTradeSection,
} from '../features/shipments/form/ShipmentFormSections';
import { NextStepsPanel, RelatedPanel, SummaryPanel } from '../features/shipments/form/ShipmentSummary';

/** Fill in a new shipment on the left; the summary on the right follows what you type */
export function NewShipmentPage() {
  const navigate = useNavigate();
  const tasks = useClaudeTasks();
  const [form, setForm] = useState<ShipmentForm>(initialShipmentForm);
  const set = (patch: Partial<ShipmentForm>) => setForm((f) => ({ ...f, ...patch }));
  // the SRB code starts as the next free one on the tracker, until you type your own
  const next = useNextSrb();
  const props = { form: { ...form, srb: form.srb || next }, set };
  // shipments live in the Serb Mind vault, which Claude writes: the form goes to Claude as a task
  const create = useAction(tasks.add, {
    success: `Sent to Claude. It creates ${props.form.srb || 'the shipment'} in the vault within a minute or two. Follow it under Reminders & Calendar → Claude's Tasks.`,
    onDone: () => void navigate('/shipments'),
  });
  const submit = () =>
    create.run({
      task: `Create this new shipment in the vault (tracker row and shipment note), exactly as filled in below. Do not contact anyone.\n${describeForm(props.form)}`,
      code: props.form.srb,
    });

  return (
    <Stack spacing={2}>
      <FormHeader
        back={{ to: '/shipments', label: 'Back to Shipments' }}
        title="New Shipment"
        subtitle="Enter the shipment details below. You can add quotations and documents after saving."
      >
        <Button variant="contained" disabled={create.busy} onClick={submit}>
          Create Shipment
        </Button>
      </FormHeader>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 320px' },
          alignItems: 'start',
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <BasicSection {...props} />
          <RouteTradeSection {...props} />
          <CargoSection {...props} />
          <ExtraSection {...props} />
          <NotesSection {...props} />
        </Stack>
        <Stack spacing={2}>
          <SummaryPanel form={props.form} />
          <NextStepsPanel />
          <RelatedPanel />
        </Stack>
      </Box>
    </Stack>
  );
}
